import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const DEFAULT_INTERVAL_SECONDS = 5;

/**
 * Dev preview toggle for skeleton screens. With `?skeleton` in the URL, returns
 * a boolean that flips every N seconds — skeleton, then real screen, then
 * skeleton again — so a skeleton can be compared against the page it stands in
 * for. The interval defaults to 5s and can be overridden via the param value,
 * e.g. `?skeleton=30` flips every 30s. Always false without the param. Combine
 * with the real loading condition, e.g. `isLoading || useSkeletonTesting()`.
 *
 * Dev-only: gated on `import.meta.env.DEV` so it is a no-op in production
 * bundles. Without the gate, anyone landing on a URL with `?skeleton` would
 * oscillate the app between the boot skeleton and real content every N seconds
 * — and since AppRoutes early-returns the skeleton, that blocks navigation.
 *
 * UI machinery hook (owns its own interval), so business components stay free
 * of `useState`/`useEffect`.
 */
export function useSkeletonTesting(): boolean {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const enabled = import.meta.env.DEV && params.has("skeleton");
  const seconds = Number(params.get("skeleton"));
  const intervalMs =
    Number.isFinite(seconds) && seconds > 0
      ? seconds * 1000
      : DEFAULT_INTERVAL_SECONDS * 1000;
  const [showSkeleton, setShowSkeleton] = useState(true);

  useEffect(() => {
    if (!enabled) {
      // Reset so a later re-activation always starts on the skeleton.
      setShowSkeleton(true);
      return;
    }
    const timer = setInterval(() => {
      setShowSkeleton((prev) => !prev);
    }, intervalMs);
    return () => {
      clearInterval(timer);
    };
  }, [enabled, intervalMs]);

  return enabled && showSkeleton;
}
